// Context composition shared by every client entry (browser, native, edge):
// the surface stamp, first-party timestamp, user id resolution, and the
// session-attribution slot (`session_cid`).
//
// Transport-free by design (and by lint rule): no SDK imports here.

import type { LissenClient } from "./events";

/**
 * Session-scoped attribution. `cid` is the click/share id that started the
 * current session's journey (rode in on the URL); once set, it is stamped as
 * `session_cid` on every subsequent event from this runtime. Attribution is
 * never computed at capture — acquisition/conversion/viral are SQL over the
 * raw events downstream.
 */
export type SessionAttribution = {
  cid?: string;
};

let sessionAttribution: SessionAttribution = {};

/** Merge attribution into the session slot (last write per key wins). */
export function setSessionAttribution(attribution: SessionAttribution): void {
  sessionAttribution = { ...sessionAttribution, ...attribution };
}

export function getSessionAttribution(): SessionAttribution {
  return { ...sessionAttribution };
}

/** Test-only helper; production code never clears attribution mid-session. */
export function clearSessionAttribution(): void {
  sessionAttribution = {};
}

/**
 * Stamps the shared enrichment onto an event payload:
 * - `lsn_timestamp` — first-party UTC ISO timestamp at call time.
 * - `lissen_client` — the surface identifier.
 * - `user_id` — only when the surface configured a `getUserId` resolver
 *   (mobile resolves the Firebase uid; falls back to a `userId` already in the
 *   payload, then ""). Surfaces relying on SDK identify() omit the resolver.
 * - `session_cid` — when session attribution holds a cid.
 *
 * The event's own properties spread last, so an explicit payload key always
 * wins over a stamp (preserves the historical mobile `track` semantics).
 */
export function composeEventProperties(args: {
  surface: LissenClient;
  getUserId?: () => string | null | undefined;
  properties: Record<string, unknown>;
}): Record<string, unknown> {
  const stamped: Record<string, unknown> = {
    lsn_timestamp: new Date().toISOString(),
    lissen_client: args.surface,
  };

  if (args.getUserId) {
    const fromPayload = args.properties["userId"];
    stamped["user_id"] =
      args.getUserId() ??
      (typeof fromPayload === "string" ? fromPayload : "");
  }

  const { cid } = sessionAttribution;
  if (cid) {
    stamped["session_cid"] = cid;
  }

  return { ...stamped, ...args.properties };
}
